import { Fragment, useEffect, useState } from "react";
import ganttChartService from "../../../service/ganttChartService";
import { useAppSelector } from "../../../store/hooks";
import { ganttChartVerticalValues } from "../../../utils/ganttChartValues";
import VerticalWorkTask from "./VerticalWorkTask";
import UnplannedTaskChartPositionAllocationService, {
  GroupedTask,
} from "../../../service/UnplannedTaskChartPositionAllocationService";
import {
  AuxiliaryTask,
  UnplannedTasksType,
  WorkTask,
} from "../../../types/ganttChart";

const UnplannedTasks = ({
  unplannedTasks,
  taskWidthPx,
}: {
  unplannedTasks: UnplannedTasksType;
  taskWidthPx: number;
}) => {
  const { ganttChartData, selectedGanttChartTab, verticalColumnWidth } =
    useAppSelector((state) => state.ganttChartSlice);
  const { timelineRange, pixelPerMinute } = useAppSelector(
    (state) => state.timelineSlice,
  );
  const [unplanned, setUnplanned] = useState<GroupedTask[]>();

  useEffect(() => {
    if (!timelineRange) return;
    // Separate tasks into full-day tasks and shorter tasks which need to be laid out
    const separatedTasks =
      UnplannedTaskChartPositionAllocationService.separateFullDayTasks(
        [...unplannedTasks.workTasks, ...unplannedTasks.operationalTasks],
        timelineRange,
      );
    const groupedTasks =
      UnplannedTaskChartPositionAllocationService.clusterOverlappingTasks(
        separatedTasks.shorterTasks,
        (task: WorkTask) => new Date(task.start),
        (task: WorkTask) => new Date(task.end),
      );
    setUnplanned(groupedTasks);
    // Check the largest gangttPosition
    const highestGanttChartPosition = (groupedTasks: GroupedTask[]): number => {
      return Math.max(...groupedTasks.map((task) => task.ganttChartPosition));
    };

    // Only use the original ganttChartData processing when NOT in Custom tab
    // In Custom tab, we should use the processed unplannedTasks prop which comes from customGanttChartData
    if (ganttChartData && selectedGanttChartTab !== "Custom") {
      setUnplanned(
        UnplannedTaskChartPositionAllocationService.getGroupedTasks(
          ganttChartData,
        ),
      );
    }
  }, [unplannedTasks, timelineRange, ganttChartData, selectedGanttChartTab]);

  return (
    <>
      {timelineRange &&
        unplanned &&
        unplanned.map((task, index) => (
          <Fragment key={"nexus-unplanned" + index}>
            <VerticalWorkTask
              hideViolations
              id={task.ganttChartPosition}
              x={
                ganttChartVerticalValues.timelineColumn +
                (taskWidthPx! + 1) * task.ganttChartPosition
              }
              task={task}
              y={ganttChartVerticalValues.findTaskYPosition(
                timelineRange!,
                pixelPerMinute,
                task.start,
              )}
              width={taskWidthPx!}
              defaultWidth={ganttChartVerticalValues.getTaskWidth(
                verticalColumnWidth,
              )}
              height={
                ganttChartService.timeDifferenceInMinutes(
                  task.start,
                  task.end,
                ) * pixelPerMinute
              }
            />
          </Fragment>
        ))}
    </>
  );
};

export default UnplannedTasks;
